import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly fullWidth?: boolean;
  readonly loading?: boolean;
  readonly iconLeft?: ReactNode;
}

/**
 * Il fondo del bottone primario e' `--brand-strong`, non `--brand`: su `--brand`
 * il testo bianco arriva solo a 3:1, sotto la soglia AA per il testo normale.
 * Il vincolo e' verificato da `packages/ui/test/contrast.spec.ts`.
 */
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--brand-strong)] text-[var(--on-brand)] hover:bg-[var(--brand-press)] active:bg-[var(--brand-press)]',
  secondary: 'bg-surface text-text border border-border hover:bg-surface-sunken',
  ghost: 'bg-transparent text-text-muted hover:bg-surface-sunken',
  danger: 'bg-[var(--budget-blocked)] text-white hover:brightness-95',
};

/** Area tap minima 48px: sopra il minimo WCAG 2.2 (44px). */
const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'min-h-[48px] px-5 text-[15px]',
  lg: 'min-h-[56px] px-6 text-[17px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', fullWidth, loading, iconLeft, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled === true || loading === true}
      // Lo stato di caricamento e' annunciato, non solo animato.
      aria-busy={loading === true ? true : undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-full font-medium',
        'transition-colors duration-[var(--dur-fast)]',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth === true ? 'w-full' : '',
        className,
      )}
      {...rest}
    >
      {iconLeft !== undefined && iconLeft !== null ? (
        <span aria-hidden="true" className="shrink-0">
          {iconLeft}
        </span>
      ) : null}
      {children}
    </button>
  );
});
